import ProgressBar from "@/routes/apt/[apt_code]/analysis/_components/weekly-summary/progress-bar"
import { analysisStore } from "@/routes/apt/[apt_code]/analysis/analysis-store"
import { langStore } from "@/stores/lang-store"
import { formatMessage } from "@/utils/format-message"
import { useEffect, useState } from "react"
import { aptSearchStore } from "@/routes/select/apt/apt-search-store"
import { getThisMonthWaterBill } from "@/routes/apt/[apt_code]/water-bill-util"
import { weeklySummaryBundle } from "./weekly-summary-const"
import Wrapper from "@/components/wrapper"

const WeeklySummary = ({ className = "" }) => {
  const { lang } = langStore()

  const { pickApt } = aptSearchStore()
  const { region_key, unit_count } = pickApt
  const { selectedDong, filteredLeakUnits, dongCountMap } = analysisStore()

  const normal = selectedDong === "all" ? unit_count : (dongCountMap[selectedDong] ?? 0)

  const leakUnits = filteredLeakUnits
  const leakCount = leakUnits?.length ?? 0

  const usageVolume = leakUnits.reduce((total, unit) => total + (Number(unit.usage_volume) || 0), 0)
  const leakVolume = leakUnits.reduce((total, unit) => total + (Number(unit.leak_volume) || 0), 0)

  const usageAmount = getThisMonthWaterBill(region_key, usageVolume, true)
  const leakAmount = getThisMonthWaterBill(region_key, leakVolume, true)
  const leakRate = usageAmount > 0 ? (leakAmount / usageAmount) * 100 : 0

  const [normalAnimated, setNormalAnimated] = useState(unit_count)
  const [leakAnimated, setLeakAnimated] = useState(0)

  useEffect(() => {
    const duration = 900

    const startNormal = normalAnimated
    const endNormal = Math.max(0, normal - leakCount)

    const startLeak = leakAnimated
    const endLeak = leakCount

    let frameId

    const start = performance.now()

    const animate = (time) => {
      const progress = Math.min((time - start) / duration, 1)

      const eased = 1 - Math.pow(1 - progress, 20)

      setNormalAnimated(Math.round(startNormal + (endNormal - startNormal) * eased))

      setLeakAnimated(Math.round(startLeak + (endLeak - startLeak) * eased))

      if (progress < 1) {
        frameId = requestAnimationFrame(animate)
      }
    }

    frameId = requestAnimationFrame(animate)

    return () => cancelAnimationFrame(frameId)
  }, [normal, leakCount, leakAnimated, normalAnimated])

  return (
    <Wrapper.shadow className={className}>
      <article className="flex justify-between items-end">
        <h3 className="text-sm font-medium text-gray-500">
          {weeklySummaryBundle.statusTitle[lang]}
        </h3>

        <span className="text-xs text-gray-400">
          {formatMessage(weeklySummaryBundle.totalUnits[lang], {
            total: unit_count
          })}
        </span>
      </article>

      {/* 정상 세대 / 누수 의심 */}
      <article className="grid grid-cols-2 gap-4 pt-1 px-1">
        <div className="flex flex-col">
          <span className="text-[10px] text-gray-400 flex items-center gap-1">
            <span className="w-2 h-2 bg-blue-500 rounded-full" />
            {weeklySummaryBundle.normalUnits[lang]}
          </span>

          <span className="text-lg font-bold text-blue-500">{normalAnimated}</span>
        </div>

        <div className="flex flex-col">
          <span className="text-[10px] text-gray-400 flex items-center gap-1">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            {weeklySummaryBundle.suspectedLeaks[lang]}
          </span>

          <span className="text-lg font-bold text-red-500">{leakAnimated}</span>
        </div>
      </article>

      <article className=" pt-2 px-1 border-t border-gray-100">
        <span className="text-xs text-gray-400">
          {weeklySummaryBundle.expectedLossAmount[lang]}
        </span>
        <div className="flex justify-between items-end">
          <span className="text-xl font-bold text-red-500">
            {leakAmount.toLocaleString()}
            {weeklySummaryBundle.currencyUnit[lang]}
          </span>

          <span className="text-xs text-gray-400">
            {formatMessage(weeklySummaryBundle.leakRatePercentage[lang], {
              rate: leakRate.toFixed(1)
            })}
          </span>
        </div>

        <ProgressBar target={leakRate} />

        <div className="flex justify-between items-center mt-2">
          <span className="text-xs text-gray-400">{weeklySummaryBundle.waterUsageBill[lang]}</span>

          <span className="text-lg font-bold text-gray-700">
            {usageAmount.toLocaleString()}
            {weeklySummaryBundle.currencyUnit[lang]}
          </span>
        </div>
      </article>
    </Wrapper.shadow>
  )
}

export default WeeklySummary
